import apiClient from "../../../../helpers/apiClient";

/*
 * Extending a reservation's drop-off from the dashboard. Mirrors the booking frontend's extend
 * flow: the whole period (original pickup → new drop-off) is re-priced with invoice-v2, what is
 * owed is the new total minus what has already been paid, and the extension itself is applied by
 * the backend — directly for cash / nothing due, or after the card payment clears.
 */

const toId = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    return String(value._id ?? value.id ?? value.locationId ?? "");
};

const unwrap = (data, fallback) => {
    if (!data?.success) {
        throw new Error(data?.message || fallback);
    }
    return data?.data;
};

/** invoice-v2 expects the category ObjectId. */
const resolveCategoryId = (reservation) => toId(reservation?.category) || toId(reservation?.carSelected?.category);

const resolveCarMongoId = async (reservation) => {
    const carId = reservation?.carSelected?._id || reservation?.carLinked?.[0]?._id || "";
    if (carId) return String(carId);

    const lookupKey = reservation?.carSelected?.VIN;
    if (!lookupKey) return "";
    const { data } = await apiClient.get(`/api/car/${lookupKey}`);
    return data?.data?._id ? String(data.data._id) : "";
};

const resolveLocationId = (reservation) =>
    toId(reservation?.pickupLocation) ||
    toId(reservation?.carSelected?.location) ||
    reservation?.carSelected?.baseLocationId ||
    "";

/** Add-ons on the reservation, minus the insurance (it is priced separately via `insuranceId`). */
const buildAddonIdList = (reservation) => {
    const insuranceId = reservation?.addonsInsuranceId || "";
    return Array.isArray(reservation?.addons)
        ? reservation.addons.map(toId).filter((id) => id && id !== insuranceId)
        : [];
};

/**
 * Everything invoice-v2 and the extension endpoints need to re-price the reservation.
 *
 * @returns {Promise<{ category: string, carId: string, locationId: string, addonIdList: string[], insuranceId: string }>}
 */
export const resolveExtensionInvoiceContext = async (reservation) => {
    let category = resolveCategoryId(reservation);
    const carId = await resolveCarMongoId(reservation);

    if (!category && carId) {
        const { data } = await apiClient.get(`/api/car/${reservation?.carSelected?.VIN || carId}`);
        category = toId(data?.data?.category);
    }

    if (!carId) throw new Error("Missing vehicle for extension invoice.");
    if (!category) throw new Error("Missing vehicle category for extension invoice.");

    return {
        category,
        carId,
        locationId: resolveLocationId(reservation),
        addonIdList: buildAddonIdList(reservation),
        insuranceId: reservation?.addonsInsuranceId || "",
    };
};

/** Prices the full extended period. Returns the invoice (charges, taxes, total, ...). */
export const quoteReservationExtension = async (reservation, context, newDropoffIso) => {
    const { data } = await apiClient.post("/api/reservation/invoice-v2", {
        pickupDate: new Date(reservation?.pickupDate),
        dropoffDate: new Date(newDropoffIso),
        category: context.category,
        location: context.locationId,
        addonIdList: context.addonIdList,
        carId: context.carId,
        insuranceId: context.insuranceId,
    });

    const invoice = unwrap(data, "Could not generate extension invoice.")?.invoice;
    if (!invoice) {
        throw new Error(data?.data?.message || "Could not generate extension invoice.");
    }
    return invoice;
};

/** What the extension costs on top of what the customer has already paid. */
export const getExtensionDueAmount = (invoice, reservation) => {
    const alreadyPaid = Number(reservation?.invoice?.deposit || 0);
    return Math.max(0, Math.round(((Number(invoice?.total) || 0) - alreadyPaid) * 100) / 100);
};

/**
 * Applies the extension now. Used for cash (records the payment first) and for extensions with
 * nothing owed. Sending `extensionInvoiceMeta` makes the server rebuild the invoice itself, keep
 * extra charges, check car conflicts and operational hours, and send the extension email.
 *
 * @returns {Promise<object>} the updated reservation
 */
export const finalizeReservationExtension = async (reservationId, { dropoff, context, cashAmount = 0 }) => {
    const amount = Number.parseFloat(Number(cashAmount).toFixed(2));

    const { data } = await apiClient.patch(`/api/reservation/extend-reservation-time/${reservationId}`, {
        dropoffDate: dropoff,
        exactDropoffTime: dropoff,
        extensionInvoiceMeta: context,
        ...(amount > 0 && { cashPayment: { paymentMethod: "cash", amount } }),
    });

    return unwrap(data, "Failed to extend reservation");
};

/** Other reservations and blocks on this car, for availability. `carId` is the car's `id`. */
export const fetchCarCalendarBlocks = async (carId) => {
    const { data } = await apiClient.get(`/api/car/${carId}/calender-blocks`);
    return data?.data || null;
};
